import * as path from 'path';
import fs from 'fs-extra';
import { BaseAdapter, BuildContext, AdapterOptions } from './BaseAdapter.js';

export interface StaticAdapterOptions extends AdapterOptions {
	spa?: boolean;
	fallback?: string;
	redirects?: Array<{ from: string; to: string; status?: number }>;
	headers?: Record<string, Record<string, string>>;
}

export class StaticAdapter extends BaseAdapter {
	private staticOptions: StaticAdapterOptions;

	constructor(options: StaticAdapterOptions = {}) {
		super('Static', 'static', options);
		this.staticOptions = {
			spa: true,
			fallback: 'index.html',
			redirects: [],
			headers: {},
			...options,
		};
	}

	async prepareBuild(context: BuildContext): Promise<any> {
		this.log('Preparing static site build...');

		return {
			build: {
				outDir: this.options.outDir,
				rollupOptions: {
					output: {
						manualChunks: {
							vendor: ['vue', 'vue-router'],
						},
					},
				},
			},
			base: this.options.publicPath,
		};
	}

	async processBuild(context: BuildContext): Promise<void> {
		this.log('Processing static build output...');

		const distPath = path.join(context.cwd, context.outDir);

		// Generate fallback page for SPA routing
		if (this.staticOptions.spa && this.staticOptions.fallback) {
			await this.generateSpaFallback(distPath);
		}

		// Apply compression if enabled
		if (this.options.compression) {
			await this.compressAssets(distPath);
		}
	}

	async generateDeploymentFiles(context: BuildContext): Promise<void> {
		this.log('Generating static deployment files...');

		const distPath = path.join(context.cwd, context.outDir);

		// Generate _redirects for Netlify
		await this.generateNetlifyRedirects(distPath);

		// Generate vercel.json for Vercel
		await this.generateVercelConfig(distPath);

		// Generate .htaccess for Apache
		await this.generateApacheConfig(distPath);
	}

	async validateDeployment(context: BuildContext): Promise<boolean> {
		this.log('Validating static deployment...');

		const distPath = path.join(context.cwd, context.outDir);
		const indexPath = path.join(distPath, 'index.html');

		if (!await fs.pathExists(indexPath)) {
			this.logError('index.html not found in build output');
			return false;
		}

		this.log('Static deployment validation passed');
		return true;
	}

	private async generateSpaFallback(distPath: string): Promise<void> {
		const indexPath = path.join(distPath, 'index.html');
		const fallbackPath = path.join(distPath, this.staticOptions.fallback!);

		if (await fs.pathExists(indexPath) && this.staticOptions.fallback !== 'index.html') {
			await fs.copy(indexPath, fallbackPath);
			this.log(`Generated SPA fallback: ${this.staticOptions.fallback}`);
		}
	}

	private async compressAssets(distPath: string): Promise<void> {
		// Note: In a real implementation, we'd use compression libraries like gzip/brotli
		this.log('Asset compression would be applied here');
	}

	private async generateNetlifyRedirects(distPath: string): Promise<void> {
		const redirectsContent = [
			// SPA fallback
			'/*    /index.html   200',
			// Custom redirects
			...this.staticOptions.redirects!.map(redirect =>
				`${redirect.from}    ${redirect.to}    ${redirect.status || 302}`
			),
		].join('\n');

		const redirectsPath = path.join(distPath, '_redirects');
		await fs.writeFile(redirectsPath, redirectsContent);
		this.log('Generated _redirects for Netlify');
	}

	private async generateVercelConfig(distPath: string): Promise<void> {
		const config = {
			rewrites: [
				{ source: '/(.*)', destination: '/index.html' }
			],
			headers: Object.entries(this.staticOptions.headers || {}).map(([source, headers]) => ({
				source,
				headers: Object.entries(headers).map(([key, value]) => ({ key, value }))
			}))
		};

		const configPath = path.join(distPath, 'vercel.json');
		await fs.writeJSON(configPath, config, { spaces: 2 });
		this.log('Generated vercel.json for Vercel');
	}

	private async generateApacheConfig(distPath: string): Promise<void> {
		const htaccessContent = `
RewriteEngine On
RewriteBase ${this.options.publicPath}

# Handle client-side routing
RewriteCond %{REQUEST_FILENAME} !-f
RewriteCond %{REQUEST_FILENAME} !-d
RewriteRule . /index.html [L]

# Security headers
Header always set X-Content-Type-Options nosniff
Header always set X-Frame-Options DENY
Header always set X-XSS-Protection "1; mode=block"

# Cache static assets
<FilesMatch "\\.(css|js|png|jpg|jpeg|gif|svg|woff|woff2)$">
    ExpiresActive On
    ExpiresDefault "access plus 1 year"
</FilesMatch>
`.trim();

		const htaccessPath = path.join(distPath, '.htaccess');
		await fs.writeFile(htaccessPath, htaccessContent);
		this.log('Generated .htaccess for Apache');
	}
} 